import { useState } from 'react';
import { useAppStore, recalculateStore } from '@/store/useAppStore';
import { validateCustomConfig } from '@gpa-scales/shared-logic/engine/customWeighting';
import { CourseType } from '@gpa-scales/shared-types';
import type { CustomWeightingConfig } from '@gpa-scales/shared-types';
import { ScrubInput } from 'calc-controls';

const COURSE_TYPE_LABELS: Record<string, string> = {
  [CourseType.Standard]: 'Standard',
  [CourseType.Honors]: 'Honors',
  [CourseType.AP]: 'AP',
  [CourseType.IB_HL]: 'IB HL',
  [CourseType.IB_SL]: 'IB SL',
  [CourseType.DualEnrollment]: 'Dual Enrollment',
  [CourseType.College]: 'College',
};

const DEFAULT_CONFIG: CustomWeightingConfig = {
  bonusPoints: {
    [CourseType.Honors]: 0.5,
    [CourseType.AP]: 1.0,
    [CourseType.IB_HL]: 1.0,
    [CourseType.IB_SL]: 0.5,
    [CourseType.DualEnrollment]: 0.5,
  },
  scaleMaximum: 5.0,
};

export function CustomWeightingEditor() {
  const settings = useAppStore((s) => s.settings);
  const updateSettings = useAppStore((s) => s.updateSettings);

  const [config, setConfig] = useState<CustomWeightingConfig>(
    settings.customWeighting ?? { ...DEFAULT_CONFIG }
  );
  const [errors, setErrors] = useState<string[]>([]);

  const updateBonus = (courseType: CourseType, value: number) => {
    setConfig((prev) => ({
      ...prev,
      bonusPoints: { ...prev.bonusPoints, [courseType]: value },
    }));
  };

  const updateScaleMax = (value: number) => {
    setConfig((prev) => ({ ...prev, scaleMaximum: value }));
  };

  const updateCap = (maxHonors: number) => {
    setConfig((prev) => ({
      ...prev,
      pointCap: { ...prev.pointCap, maxHonorsPoints: maxHonors },
    }));
  };

  const save = () => {
    const validationErrors = validateCustomConfig(config);
    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      return;
    }
    setErrors([]);
    updateSettings({ customWeighting: config });
    recalculateStore();
  };

  const clear = () => {
    setConfig({ ...DEFAULT_CONFIG });
    const updated = { ...useAppStore.getState().settings };
    delete updated.customWeighting;
    useAppStore.getState().loadSettings(updated);
    recalculateStore();
  };

  return (
    <div className="rounded-lg border border-border bg-surface p-4">
      <h3 className="mb-1 text-sm font-semibold text-[var(--color-text)]">Custom Weighting</h3>
      <p className="mb-3 text-xs text-[var(--color-text-muted)]">
        Define bonus points per course type and an optional honors point cap for a custom weighting scheme.
      </p>

      <div className="space-y-3">
        {/* Bonus points per course type */}
        <div>
          <p className="mb-1 text-xs font-medium text-[var(--color-text-secondary)]">Bonus Points by Course Type</p>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            {Object.values(CourseType).map((ct) => (
              <div key={ct} className="flex items-center gap-2">
                <span className="text-xs text-[var(--color-text)]">{COURSE_TYPE_LABELS[ct]}</span>
                <ScrubInput
                  value={config.bonusPoints[ct] ?? 0}
                  onChange={(v) => updateBonus(ct, v)}
                  label={`${COURSE_TYPE_LABELS[ct]} bonus points`}
                  min={0}
                  step={0.1}
                  decimals={1}
                  inline
                />
              </div>
            ))}
          </div>
        </div>

        {/* Scale maximum */}
        <div className="flex items-center gap-3">
          <label htmlFor="scale-maximum" className="text-xs text-[var(--color-text-secondary)]">Scale Maximum</label>
          <ScrubInput
            value={config.scaleMaximum}
            onChange={updateScaleMax}
            label="Scale Maximum"
            min={4}
            max={10}
            step={0.5}
            decimals={1}
            inline
          />
        </div>

        {/* Point cap */}
        <div className="flex items-center gap-3">
          <label htmlFor="max-honors-points" className="text-xs text-[var(--color-text-secondary)]">Max Honors Points</label>
          <ScrubInput
            value={config.pointCap?.maxHonorsPoints ?? 8}
            onChange={updateCap}
            label="Max Honors Points"
            min={0}
            max={20}
            step={1}
            decimals={0}
            inline
          />
          <button
            onClick={() => setConfig((prev) => {
              if (prev.pointCap) {
                const { pointCap: _cap, ...rest } = prev;
                return rest;
              }
              return { ...prev, pointCap: { maxHonorsPoints: 8 } };
            })}
            className="text-xs text-primary hover:text-primary-hover"
          >
            {config.pointCap ? 'Remove Cap' : 'Set Cap'}
          </button>
        </div>

        {/* Actions */}
        <div className="flex gap-2">
          <button onClick={save} className="rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-white">
            Apply
          </button>
          <button onClick={clear} className="rounded-md px-3 py-1.5 text-xs text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-tertiary)]">
            Reset to Defaults
          </button>
        </div>

        {/* Errors */}
        {errors.length > 0 && (
          <div className="rounded border border-error bg-error-light p-2">
            {errors.map((err, i) => (
              <p key={i} className="text-xs text-error">{err}</p>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
